import React, { useState, useEffect, useRef, useCallback } from "react";
import GoogleMapReact from "google-map-react";
import { useDispatch, useSelector } from "react-redux";
import positionImage from "@assets/random/position-icon.png";
import navigationImage from "@assets/random/navigation-icon.png";
import { setUserCurrentLocation } from "../../../../appRedux/actions/User";
import { setPropertyInWidget } from "../../../../appRedux/actions/Actions";
import IntlMessages from "@util/IntlMessages";
import ListMapPositionMarker from "./ListMapPositionMarker";
import ListMapProfileButton from "./ListMapProfileButton";
import { getDistanceFromLatLngInKm } from "@util/Scripts";
import "./mapComponent.less";
import { Spin, Popover } from "../../../BP2Components";
import { MyPositionMarker } from "./MyPositionMarker";

const MapComponent = (value) => {
   const { oldId } = useSelector(({ user }) => user);
   const [currentPosition, setCurrentPosition] = useState(null);
   const [centerPosition, setCenterPosition] = useState(null);
   const [lastPosition, setLastPosition] = useState(null);
   const [defaultZoom, setDefaultZoom] = useState(14);
   const [even, setEven] = useState(false);
   const [markerId, setMarkerId] = useState();
   const dispatch = useDispatch();
   const [radiusMeters, setRadiusMeters] = useState(null);

   const mapRef = useRef(null);
   const mapsRef = useRef(null);
   const circleRef = useRef(null);
   const mapCenter = currentPosition ?? centerPosition;
   const realCurrentPositionRef = useRef(null);
   const userInteractedRef = useRef(false);
   const listenersRef = useRef([]);
   const lastFitKeyRef = useRef("");

   const DEFAULT_CENTER = { lat: 52.2297, lng: 21.0122 };
   const DEFAULT_ZOOM = 6;

   const roundWithCustomThreshold = (value, threshold = 0.6) => {
      if (typeof value !== "number" || Number.isNaN(value)) {
         return 0;
      }

      const integerPart = Math.trunc(value);

      const decimalPart = Math.round((value - integerPart) * 10) / 10;

      return decimalPart >= threshold ? integerPart + 1 : integerPart;
   };

   const getDistanceInMeters = (pointA, pointB) => {
      if (!pointA || !pointB) return 0;

      const earthRadiusMeters = 6371000;
      const toRadians = (degrees) => (degrees * Math.PI) / 180;

      const latDiff = toRadians(pointB.lat - pointA.lat);
      const lngDiff = toRadians(pointB.lng - pointA.lng);

      const latARadians = toRadians(pointA.lat);
      const latBRadians = toRadians(pointB.lat);

      const sinLat = Math.sin(latDiff / 2);
      const sinLng = Math.sin(lngDiff / 2);

      const angleFactor =
         sinLat * sinLat + Math.cos(latARadians) * Math.cos(latBRadians) * sinLng * sinLng;

      const centralAngle = 2 * Math.atan2(Math.sqrt(angleFactor), Math.sqrt(1 - angleFactor));

      const distanceInMeters = earthRadiusMeters * centralAngle;

      return roundWithCustomThreshold(distanceInMeters, 0.6);
   };

   const fitToMarkers = useCallback(() => {
      const map = mapRef.current;
      const maps = mapsRef.current;
      if (!map || !maps) return;

      if (!value.markerList || !value.listProps?.localizationFieldName) return;

      const lCLocalizationFieldName = value.listProps?.localizationFieldName.toLowerCase();
      const bounds = new maps.LatLngBounds();

      let hasAny = false;

      value.markerList.forEach((marker) => {
         const raw = marker?.[lCLocalizationFieldName];
         if (!raw) return;

         const parts = String(raw).replaceAll(":", ",").split(",");
         const lat = Number(parts[0]);
         const lng = Number(parts[1]);

         if (Number.isFinite(lat) && Number.isFinite(lng)) {
            bounds.extend(new maps.LatLng(lat, lng));
            hasAny = true;
         }
      });

      if (!hasAny || bounds.isEmpty()) return;

      map.fitBounds(bounds, 60);

      maps.event.addListenerOnce(map, "bounds_changed", () => {
         const maxZoom = 17;
         if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
      });
   }, [value.markerList, value.listProps?.localizationFieldName]);

   const restorePosition = () => {
      userInteractedRef.current = false;
      fitToMarkers();
   };

   const navigateToTheClosestPin = () => {
      let rows = value.listState.rows;
      const allActions = value.listState?.allActions;
      if (rows?.length > 0 && allActions && value.listProps.localizationFieldName) {
         const navigateAction = allActions.find((action) => action?.navUrl?.includes("https"));
         if (navigateAction) {
            const navigateURL = navigateAction.navUrl.replace("destination", "origin");
            const lCLocalizationFieldName = value.listProps?.localizationFieldName.toLowerCase();
            const allMarkers = [];
            const currentLocalization = `${currentPosition?.lat.toString()},${currentPosition?.lng.toString()}`;

            value?.markerList.forEach((marker, i) => {
               allMarkers.push({
                  lat: +marker[lCLocalizationFieldName].replaceAll(":", ",").split(",")[0],
                  lng: +marker[lCLocalizationFieldName].replaceAll(":", ",").split(",")[1],
               });
            });

            const allDistances = allMarkers.map((marker) =>
               getDistanceFromLatLngInKm(currentPosition, marker),
            );
            const requiredMarker =
               allMarkers[allDistances.findIndex((item) => item === Math.min(...allDistances))];

            const theNearestLocalization = `${requiredMarker.lat.toString()},${requiredMarker.lng.toString()}`;
            const fullNavigateURL = `${navigateURL}${currentLocalization}&destination=${theNearestLocalization}`;
            window.location.href = fullNavigateURL;
         }
      }
   };

   const success = (position) => {
      let pos = {
         lat: position.coords.latitude,
         lng: position.coords.longitude,
      };
      const stringPosition = position.coords.latitude + ", " + position.coords.longitude;

      if (JSON.stringify(lastPosition) !== JSON.stringify(pos)) {
         setLastPosition(pos);
         dispatch(setUserCurrentLocation(stringPosition));
      }
      realCurrentPositionRef.current = pos;
      setTimeout(() => {
         setCenterPosition(pos);
         setCurrentPosition(pos);
      }, 1000);
   };

   useEffect(() => {
      const updateUserLocation = (position) => {
         const stringPosition = position.coords.latitude + ", " + position.coords.longitude;
      };
      navigator.geolocation.getCurrentPosition(updateUserLocation);
   }, []);

   useEffect(() => {
      if (+value.listState.defaultZoomLevel && +value.listState.defaultZoomLevel !== defaultZoom) {
         setDefaultZoom(+value.listState.defaultZoomLevel);
         setEven(!even);
      }
   }, [value.listState.defaultZoomLevel]);

   useEffect(() => {
      navigator.geolocation.getCurrentPosition(success);
   }, []);

   useEffect(() => {
      const map = mapRef.current;
      const maps = mapsRef.current;
      if (!map || !maps) return;

      const selectedCoords = value.selectedRowCoordinates || [];

      if (selectedCoords.length > 0) {
         if (selectedCoords.length === 1) {
            map.panTo({ lat: selectedCoords[0].lat, lng: selectedCoords[0].lng });
            map.setZoom(16);
         } else {
            const centerLat =
               selectedCoords.reduce((sum, c) => sum + c.lat, 0) / selectedCoords.length;
            const centerLng =
               selectedCoords.reduce((sum, c) => sum + c.lng, 0) / selectedCoords.length;
            const center = { lat: centerLat, lng: centerLng };

            const distances = selectedCoords.map((coord) => getDistanceInMeters(center, coord));
            const maxDistance = Math.max(...distances);

            const circleRadius = roundWithCustomThreshold(maxDistance * 1.15, 0.6);

            if (circleRef.current) {
               circleRef.current.setCenter(center);
               circleRef.current.setRadius(circleRadius);
               circleRef.current.setVisible(true);
            }

            const metersPerDegreeLat = 111320;
            const metersPerDegreeLng = 111320 * Math.cos((center.lat * Math.PI) / 180);

            const latOffset = circleRadius / metersPerDegreeLat;
            const lngOffset = circleRadius / metersPerDegreeLng;

            const circleBounds = new maps.LatLngBounds(
               new maps.LatLng(center.lat - latOffset, center.lng - lngOffset), // SW
               new maps.LatLng(center.lat + latOffset, center.lng + lngOffset), // NE
            );

            map.fitBounds(circleBounds, 30);
         }

         return;
      }

      if (circleRef.current) {
         circleRef.current.setVisible(true);
      }

      if (!value.markerList || !value.listProps?.localizationFieldName) return;

      const lCLocalizationFieldName = value.listProps?.localizationFieldName.toLowerCase();
      const key = (value.markerList || [])
         .map((marker) => {
            const raw = marker?.[lCLocalizationFieldName];
            if (!raw) return "";
            const parts = String(raw).replaceAll(":", ",").split(",");
            const lat = Number(parts[0]);
            const lng = Number(parts[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
            return `${lat.toFixed(6)},${lng.toFixed(6)}`;
         })
         .filter(Boolean)
         .join("|");

      if (!key) return;

      const locked = !!value.isMapInteractionLocked;

      if (key === lastFitKeyRef.current) return;

      if (userInteractedRef.current && !locked) return;

      lastFitKeyRef.current = key;

      requestAnimationFrame(() => {
         fitToMarkers();
      });
   }, [
      fitToMarkers,
      value.markerList,
      value.listProps?.localizationFieldName,
      value.isMapInteractionLocked,
      value.selectedRowCoordinates,
   ]);

   const handleSliderChange = (sliderValue) => {
      dispatch(
         setPropertyInWidget({
            id: value.id,
            propertyName: "distanceToFilter",
            value: sliderValue,
         }),
      );
   };

   const mark =
      value.listProps.maxDistanceToFilter !== undefined ? value.listProps.maxDistanceToFilter : 30;

   const marks = {
      0: {
         label: (
            <span className="mark p3">
               0<span className="mark-unit p3"> km</span>
            </span>
         ),
      },
      [mark]: {
         label: (
            <span className="mark p3">
               {mark?.toString()}
               <span className="mark-unit p3"> km</span>
            </span>
         ),
      },
   };

   const createMarkerList = () => {
      let markers = [];
      if (value.markerList && value.listProps?.localizationFieldName) {
         const lCLocalizationFieldName = value.listProps?.localizationFieldName.toLowerCase();
         const selectedCoords = value.selectedRowCoordinates || [];

         value.markerList?.forEach((marker, i) => {
            if (
               marker[lCLocalizationFieldName] &&
               marker[lCLocalizationFieldName] !== "" &&
               value.listProps.mapClickFilterColName
            ) {
               let icon;
               if (marker.typ) {
                  icon = marker.typ?.altUrl ?? marker.typ?.altUrl1 ?? marker.typ?.fileUrl;
               }

               const markerLat = Number(
                  marker[lCLocalizationFieldName].replaceAll(":", ",").split(",")[0],
               );
               const markerLng = Number(
                  marker[lCLocalizationFieldName].replaceAll(":", ",").split(",")[1],
               );
               const isSelected = selectedCoords.some(
                  (coord) =>
                     Math.abs(coord.lat - markerLat) < 0.0001 &&
                     Math.abs(coord.lng - markerLng) < 0.0001,
               );

               if (selectedCoords.length > 0 && !isSelected) {
                  return;
               }

               markers.push(
                  <ListMapPositionMarker
                     key={`marker-${i}`}
                     lat={markerLat}
                     lng={markerLng}
                     isSelected={isSelected}
                     group={marker.group}
                     isOpenAction={markerId === marker._sysRecordId}
                     onCloseAction={setMarkerId}
                     markerName={
                        value.markerList.find((marker) => marker._sysRecordId === markerId)?.[
                           value.listProps.mapPointColName.toLowerCase()
                        ]
                     }
                     allActions={value.allActions}
                     markerId={markerId}
                     setMarkerId={setMarkerId}
                     onClick={() => {
                        if (marker._sysRecordId) {
                           setMarkerId(marker._sysRecordId);
                        }
                        if (value.listState.rows !== null) {
                           let filterColName = value.listProps.mapClickFilterColName.toLowerCase();
                           let baseColName;
                           value.listState.columns.forEach((col) => {
                              if (col.dataIndex === filterColName) {
                                 baseColName = col.attribute;
                              }
                           });
                           let clickedTaskSerieId = null;
                           let clickedTaskSerieListId = null;
                           if (value.listProps.viewListSerie) {
                              value.listProps.listWidgetSeries.forEach((serie, i) => {
                                 if (value.listProps.viewListSerie === serie.name) {
                                    clickedTaskSerieId = serie.id;
                                    clickedTaskSerieListId = serie.listId;
                                 }
                              });
                           }
                           if (
                              value.listProps.mapIndependentSerie === value.selectedSerieObj.name
                           ) {
                              clickedTaskSerieId = value.selectedSerieObj.id;
                              clickedTaskSerieListId = value.selectedSerieObj;
                           }

                           let address,
                              mapPointColName,
                              mapAddressColName,
                              mapDistanceColName = "";
                           if (value.listProps.mapPointColName) {
                              mapPointColName = value.listProps.mapPointColName.toLowerCase();
                           }
                           if (value.listProps.mapAddressColName) {
                              mapAddressColName = value.listProps.mapAddressColName.toLowerCase();
                           }
                           if (value.listProps.mapDistanceColName) {
                              mapDistanceColName = value.listProps.mapDistanceColName.toLowerCase();
                           }
                           if (marker[value.listProps.mapPointColName]) {
                              address += marker[value.listProps.mapPointColName];
                           }
                           if (
                              marker[value.listProps.mapAddressColName] &&
                              marker[value.listProps.mapAddressColName].value
                           ) {
                              address += `, ${marker[value.listProps.mapAddressColName].value}`;
                           }
                           if (marker[value.listProps.mapDistanceColName]) {
                              address += `, ${marker[value.listProps.mapDistanceColName]}km`;
                           }
                           if (clickedTaskSerieId) {
                              value.switchToWindowTaskSerie(address);
                              value.handleSerieChange(clickedTaskSerieId);
                           }
                        }
                     }}
                     markUrl={icon}
                  />,
               );
            }
         });
      }
      return markers;
   };

   const handleChange = ({ center, bounds }) => {
      if (!center || !bounds || !bounds.nw) return;
      const topPoint = { lat: bounds.nw.lat, lng: center.lng };
      const newRadiusMeters = getDistanceInMeters(center, topPoint);
      setRadiusMeters(newRadiusMeters);
      value.handleChangeRadius?.(newRadiusMeters, center);

      if (circleRef.current) {
         circleRef.current.setCenter(center);
         circleRef.current.setRadius(newRadiusMeters);
      }
   };

   useEffect(() => {
      return () => {
         listenersRef.current.forEach((listener) => {
            if (mapsRef.current && listener) {
               mapsRef.current.event.removeListener(listener);
            }
         });
      };
   }, []);

   useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      const locked = !!value.isMapInteractionLocked;

      map.setOptions({
         draggable: !locked,
         scrollwheel: !locked,
         disableDoubleClickZoom: locked,
         keyboardShortcuts: !locked,
         clickableIcons: !locked,
         gestureHandling: locked ? "none" : "auto",
         zoomControl: !locked,
         fullscreenControl: !locked,
         streetViewControl: !locked,
         mapTypeControl: !locked,
      });
   }, [value.isMapInteractionLocked]);

   return (
      <>
         <div className="profiles-container">
            <div className="profiles">
               {value.listState.listProfiles
                  ? value.listState.listProfiles.map((profile, i) => {
                       return (
                          <div id={`map-profile-${i}`} key={`profile-${i}`}>
                             <ListMapProfileButton
                                profile={profile}
                                profileSelection={value.listState.profileSelection}
                                index={i}
                                handleClick={() => {
                                   value.profileButtonClickHandler(profile);
                                }}
                             />
                          </div>
                       );
                    })
                  : null}
            </div>
         </div>

         <div className="map-container" style={value.height ? { height: value.height } : {}}>
            <GoogleMapReact
               yesIWantToUseGoogleMapApiInternals
               bootstrapURLKeys={{
                  key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
               }}
               defaultCenter={centerPosition || DEFAULT_CENTER}
               defaultZoom={defaultZoom || DEFAULT_ZOOM}
               zoom={defaultZoom ? defaultZoom : 13}
               onClick={value.onMapClick ? value.onMapClick : null}
               onChange={handleChange}
               onGoogleApiLoaded={({ map, maps }) => {
                  mapRef.current = map;
                  mapsRef.current = maps;

                  circleRef.current = new maps.Circle({
                     map,
                     center: map.getCenter(),
                     radius: radiusMeters || 0,
                     strokeColor: "#4285F4",
                     strokeOpacity: 0.8,
                     strokeWeight: 2,
                     fillColor: "#4285F4",
                     fillOpacity: 0.1,
                  });

                  const dragListener = maps.event.addListener(map, "dragstart", () => {
                     userInteractedRef.current = true;
                  });
                  const zoomListener = maps.event.addListener(map, "zoom_changed", () => {
                     userInteractedRef.current = true;
                  });
                  listenersRef.current = [dragListener, zoomListener];

                  maps.event.addListenerOnce(map, "idle", () => {
                     if (!userInteractedRef.current) fitToMarkers();
                  });

                  if (!userInteractedRef.current) fitToMarkers();
               }}
            >
               {currentPosition ? (
                  <MyPositionMarker
                     lat={currentPosition.lat}
                     lng={currentPosition.lng}
                     text="My Marker"
                  />
               ) : null}
               {value.listProps?.localizationFieldName ? createMarkerList() : null}
            </GoogleMapReact>

            {mapRef.current && mapsRef.current ? (
               <>
                  <Popover
                     placement="left"
                     content={<IntlMessages id="map.yourPosition" />}
                     trigger="hover"
                  >
                     <div className="set-position-button" onClick={restorePosition}>
                        <img src={positionImage} className="position-icon"></img>
                     </div>
                  </Popover>
                  <Popover
                     placement="right"
                     content={<IntlMessages id="map.navigateToTheNearest" />}
                     trigger="hover"
                  >
                     <div className="nearest-task-button" onClick={navigateToTheClosestPin}>
                        <img src={navigationImage} className="position-icon"></img>
                     </div>
                  </Popover>
               </>
            ) : (
               <Spin className="gx-loader-container" />
            )}
         </div>
      </>
   );
};

export default MapComponent;
